import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Repeat, Send, CalendarClock, XCircle, Wand2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../../lib/api";

const STATUS_CLS = {
  suggested: "bg-warning/10 text-warning",
  scheduled: "bg-electric/10 text-electric",
  sent: "bg-success/10 text-success",
  ignored: "bg-secondary text-muted-foreground",
};

export default function Relances() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const [generated, setGenerated] = useState({});
  const snoozeLabels = t("relances.snooze");
  const snoozeDays = [1, 3, 7];

  const load = () => api.get("/followups").then((r) => setItems(r.data.followups)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async (f) => {
    setBusy(f.followup_id);
    try {
      const { data } = await api.post(`/followups/${f.followup_id}/generate`, {},
        { headers: { "Idempotency-Key": crypto.randomUUID() } });
      setGenerated((g) => ({ ...g, [f.followup_id]: data.email }));
    } catch (e) { toast.error(fmtErr(e, t("relances.genFail"))); }
    setBusy("");
  };

  const act = async (f, action, scheduledFor) => {
    try {
      await api.patch(`/followups/${f.followup_id}`, { action, scheduled_for: scheduledFor });
      toast.success(action === "sent" ? t("relances.toastSent") : action === "ignore" ? t("relances.toastIgnored") : t("relances.toastScheduled"));
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const schedule = (f, days) => {
    const d = new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);
    act(f, "schedule", d);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="relances-page">
      <div>
        <h1 className="font-display font-black text-3xl tracking-tighter">{t("relances.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("relances.subtitle")}</p>
      </div>
      {!items ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="skeleton-shimmer h-28 rounded-2xl" />)}</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card py-20 text-center" data-testid="relances-empty">
          <CheckCircle2 size={32} className="mx-auto text-success mb-4" />
          <p className="font-semibold text-sm">{t("relances.empty1")}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("relances.empty2")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((f, i) => (
            <motion.div key={f.followup_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="rounded-2xl border border-border bg-card p-6" data-testid={`followup-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2.5">
                    <div className="h-9 w-9 rounded-xl bg-warning/10 text-warning flex items-center justify-center"><Repeat size={15} /></div>
                    <div>
                      <p className="text-sm font-bold">{f.contact_name}</p>
                      <p className="text-[11px] text-muted-foreground">{f.contact_email}</p>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    {f.subject} — {t("relances.waiting", { days: f.days_waiting })}
                  </p>
                </div>
                <span className={`text-[10px] font-bold rounded-full px-3 py-1 ${STATUS_CLS[f.status] || STATUS_CLS.suggested}`}>
                  {t(`relances.statuses.${f.status}`)}{f.status === "scheduled" && f.scheduled_for ? ` — ${f.scheduled_for}` : ""}
                </span>
              </div>

              {generated[f.followup_id] !== undefined || f.generated_email ? (
                <textarea value={generated[f.followup_id] ?? f.generated_email}
                  onChange={(e) => setGenerated((g) => ({ ...g, [f.followup_id]: e.target.value }))}
                  rows={6} data-testid={`followup-email-${i}`}
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm outline-none focus:border-electric resize-y mb-4" />
              ) : null}

              <div className="flex flex-wrap gap-2">
                <button onClick={() => generate(f)} disabled={busy === f.followup_id} data-testid={`followup-generate-${i}`}
                  className="rounded-full border border-electric/40 text-electric text-[11px] font-bold px-4 py-2 hover:bg-electric/10 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                  <Wand2 size={12} className={busy === f.followup_id ? "animate-spin" : ""} />
                  {busy === f.followup_id ? t("relances.generating") : (generated[f.followup_id] ?? f.generated_email) ? t("relances.regenerate") : t("relances.generate")}
                </button>
                <button onClick={() => act(f, "sent")} data-testid={`followup-send-${i}`}
                  className="rounded-full bg-electric text-white text-[11px] font-bold px-4 py-2 hover:bg-blue-700 transition-colors flex items-center gap-1.5">
                  <Send size={12} /> {t("relances.send")}
                </button>
                {snoozeDays.map((days, si) => (
                  <button key={days} onClick={() => schedule(f, days)} data-testid={`followup-snooze-${days}-${i}`}
                    className="rounded-full border border-border text-[11px] font-semibold px-3.5 py-2 hover:border-warning/60 hover:text-warning transition-colors flex items-center gap-1">
                    <CalendarClock size={11} /> {snoozeLabels[si]}
                  </button>
                ))}
                <button onClick={() => act(f, "ignore")} data-testid={`followup-ignore-${i}`}
                  className="rounded-full border border-border text-[11px] font-semibold px-3.5 py-2 text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 ml-auto">
                  <XCircle size={11} /> {t("relances.ignore")}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
